import { subDays } from "date-fns";
import { Eye, Filter, MousePointerClick, Target, TrendingDown } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import {
  DEFAULT_RANGE_DAYS,
  METRICS_CONFIG,
  PAGE_CONFIG,
  TABLE_TOOLBAR_CONFIG,
  SOURCE_COLUMN_MAX_LENGTH,
  WEB_ANALYTICS_FILTER_DIMENSIONS,
  WEB_ANALYTICS_FILTER_PRESERVE_PARAM_KEYS,
  WEB_ANALYTICS_MIN_DATE,
  WEB_ANALYTICS_TRUNCATED_SOURCE_COLUMNS,
  DATE_RANGE_PICKER_CONFIG,
  getWebAnalyticsMaxDate,
  getWebAnalyticsUiComponents,
} from "./WebAnalyticsPageConfig";
import {
  useWebAnalyticsAggregated,
  useWebAnalyticsFilterCounts,
  useWebAnalyticsFilteredCount,
  useWebAnalyticsMetrics,
} from "../api/useWebAnalyticsData";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Button } from "@/components/ui/v2/button";
import { FiltersDrawer, type FilterSectionConfig } from "@/components/ui/v2/filters/FiltersDrawer";
import { type ActiveFilter, TableActiveFilters } from "@/components/ui/v2/table/table-active-filters";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/v2/tooltip";
import UnavailablePage from "@/components/ui/v2/unavailable-page";
import { URL_PARAMS } from "@/constants/urlParams";
import MetricCard from "@/features/campaigns/jobboards/components/MetricCard";
import { useModuleStatus } from "@/features/integrations/api/useModuleStatus";
import { renderComponent } from "@/features/integrations/shared/renderer";
import type { RenderContext } from "@/features/integrations/shared/renderer/types";
import type { WebAnalyticsFilters, WebAnalyticsMetrics } from "@/features/web-analytics/types";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import { useSortSearchParams, useTableSortHandler } from "@/lib/hooks/useSortSearchParams";
import { truncateWithEllipsis } from "@/lib/utils/stringUtils";
import { clampDate, formatLocalDateOnly, parseLocalDateOnly } from "@/utils/dateUtils";
import { useValueFormatter } from "@/utils/useValueFormatter";
import type { ValueFormat } from "@/utils/valueFormat";

const METRIC_ICONS: Record<(typeof METRICS_CONFIG)[number]["metricKey"], typeof Eye> = {
  totalVisits: Eye,
  averageInteractions: MousePointerClick,
  averageBounceRate: TrendingDown,
  averageConversionRate: Target,
  totalConversions: Target,
};

export default function WebAnalyticsPage() {
  const { t } = useTranslation();
  const { format: formatValue } = useValueFormatter();
  const { clientId } = useParams<{ clientId?: string }>();
  const { data: moduleStatus, isLoading: moduleLoading } = useModuleStatus("web-analytics", { clientId });

  const [searchParams, setSearchParams] = useSearchParams();
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const { searchValue, debouncedSearchValue, setSearchValue } = useSearchSearchParams();
  const { orderBy, descending } = useSortSearchParams();
  const handleSort = useTableSortHandler();

  const [filtersOpen, setFiltersOpen] = useState(false);

  // Per render, so a tab left open overnight still ends at yesterday.
  const maxDate = getWebAnalyticsMaxDate();
  // Default window: the last DEFAULT_RANGE_DAYS ending yesterday. subDays, not millisecond arithmetic:
  // a DST transition inside the window would otherwise make it 6 or 8 days instead of 7.
  const defaultTo = maxDate;
  const defaultFrom = subDays(defaultTo, DEFAULT_RANGE_DAYS - 1);
  // Clamp URL-supplied dates to [min, yesterday] so hand-edited params can't escape the allowed range.
  const startDateStr = formatLocalDateOnly(
    clampDate(parseLocalDateOnly(searchParams.get(URL_PARAMS.DATE_FROM) ?? undefined) ?? defaultFrom, WEB_ANALYTICS_MIN_DATE, maxDate),
  );
  const endDateStr = formatLocalDateOnly(
    clampDate(parseLocalDateOnly(searchParams.get(URL_PARAMS.DATE_TO) ?? undefined) ?? defaultTo, WEB_ANALYTICS_MIN_DATE, maxDate),
  );
  // From the day-precision strings, so identity is stable within a day and the queries don't refetch.
  const startDate = useMemo(() => parseLocalDateOnly(startDateStr)!, [startDateStr]);
  const endDate = useMemo(() => parseLocalDateOnly(endDateStr)!, [endDateStr]);

  // Selected filter values per dimension, read straight from the URL (multi-value).
  const filters = useMemo<WebAnalyticsFilters>(() => {
    const result: WebAnalyticsFilters = {};
    for (const dimension of WEB_ANALYTICS_FILTER_DIMENSIONS) {
      const values = searchParams.getAll(dimension);
      if (values.length > 0) result[dimension] = values;
    }
    return result;
  }, [searchParams]);

  const dataParams = {
    startDate,
    endDate,
    clientId: clientId ?? undefined,
    filters,
    searchValue: debouncedSearchValue,
  };

  const aggregatedQuery = useWebAnalyticsAggregated({
    ...dataParams,
    page,
    pageSize,
    sortColumn: orderBy ?? null,
    sortDirection: orderBy ? (descending ? "desc" : "asc") : null,
  });
  // Filter options depend on date range + client + search only — never on the active selections.
  // Passing `filters` here cascades them: picking a referrer type would drop device/country options
  // that don't co-occur with it, so reopening the drawer showed a shrunken list. Matches the
  // reporting, applies and candidates pages, which all omit selections from their counts query.
  const filterCountsQuery = useWebAnalyticsFilterCounts({
    startDate,
    endDate,
    clientId: clientId ?? undefined,
    searchValue: debouncedSearchValue,
  });
  const metricsQuery = useWebAnalyticsMetrics(dataParams);

  const tableData = useMemo(() => aggregatedQuery.data?.data ?? [], [aggregatedQuery.data?.data]);
  const totalRecords = aggregatedQuery.data?.totalRecords ?? 0;
  const ui = useMemo(() => getWebAnalyticsUiComponents(t), [t]);

  const formatMetric = useCallback(
    (metricKey: keyof WebAnalyticsMetrics, format: ValueFormat, data: WebAnalyticsMetrics | undefined) =>
      formatValue(data?.[metricKey] ?? null, format),
    [formatValue],
  );

  const setDateRange = useCallback(
    (from?: string, to?: string) => {
      const next = new URLSearchParams(searchParams);
      if (from) next.set(URL_PARAMS.DATE_FROM, from);
      else next.delete(URL_PARAMS.DATE_FROM);
      if (to) next.set(URL_PARAMS.DATE_TO, to);
      else next.delete(URL_PARAMS.DATE_TO);
      if (next.has(URL_PARAMS.PAGE)) next.set(URL_PARAMS.PAGE, "1");
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const onFilterChange = useCallback(
    (key: string, value: unknown) => {
      if (key !== TABLE_TOOLBAR_CONFIG.dateRangeFilterKey || value == null || typeof value !== "object")
        return;
      const range = value as { startDate?: string; endDate?: string };
      setDateRange(range.startDate, range.endDate);
    },
    [setDateRange],
  );

  const filterValues = useMemo(
    () => ({
      [TABLE_TOOLBAR_CONFIG.dateRangeFilterKey]: { startDate: startDateStr, endDate: endDateStr },
    }),
    [startDateStr, endDateStr],
  );

  const filterSections = useMemo<FilterSectionConfig[]>(() => {
    const counts = filterCountsQuery.data;
    if (!counts) return [];
    return WEB_ANALYTICS_FILTER_DIMENSIONS.filter((d) => counts[d]?.length).map((dimension) => ({
      key: dimension,
      title: t(`webAnalytics.filters.${dimension}`),
      options: counts[dimension],
    }));
  }, [filterCountsQuery.data, t]);

  // Chips for the applied dimension filters, shown under the toolbar; removing one edits the URL.
  const activeFilters = useMemo<ActiveFilter[]>(
    () =>
      WEB_ANALYTICS_FILTER_DIMENSIONS.flatMap((dimension) =>
        (filters[dimension] ?? []).map((value) => ({
          key: dimension,
          label: t(`webAnalytics.filters.${dimension}`),
          value,
          valueLabel: value,
        })),
      ),
    [filters, t],
  );

  const removeFilter = useCallback(
    (filter: ActiveFilter) => {
      const next = new URLSearchParams(searchParams);
      const remaining = next.getAll(filter.key).filter((v) => v !== String(filter.value));
      next.delete(filter.key);
      remaining.forEach((v) => next.append(filter.key, v));
      if (next.has(URL_PARAMS.PAGE)) next.set(URL_PARAMS.PAGE, "1");
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    WEB_ANALYTICS_FILTER_DIMENSIONS.forEach((dimension) => next.delete(dimension));
    if (next.has(URL_PARAMS.PAGE)) next.set(URL_PARAMS.PAGE, "1");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  // Live "visits found" count for the drawer header, driven by its pending (not-yet-applied) selection.
  const countBaseParams = useMemo(
    () => ({ startDate, endDate, clientId: clientId ?? undefined, searchValue: debouncedSearchValue }),
    [startDate, endDate, clientId, debouncedSearchValue],
  );
  const useFilteredCount = (pending: Record<string, string[]>) =>
    useWebAnalyticsFilteredCount(countBaseParams, pending as WebAnalyticsFilters);

  const filtersAction = (
    <Button variant="outline" size="sm" type="button" onClick={() => setFiltersOpen(true)}>
      <Filter className="h-4 w-4" />
      {t("common.filters", "Filters")}
    </Button>
  );

  // Keyed on the day so the object stays stable within a day and rebuilds when it rolls over.
  const maxDateStr = formatLocalDateOnly(maxDate);
  const dateRangePickerOptions = useMemo(
    () => ({ ...DATE_RANGE_PICKER_CONFIG, maxDate: parseLocalDateOnly(maxDateStr)! }),
    [maxDateStr],
  );

  const context = useMemo<RenderContext>(
    () => ({
      integrationName: "matomo",
      moduleName: "web-analytics",
      clientId: clientId ?? undefined,
      executeAction: async () => {},
      tableData,
      isLoading: aggregatedQuery.isLoading || aggregatedQuery.isFetching,
      pagination: {
        page,
        pageSize,
        totalRecords,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
      },
      orderBy: orderBy ?? undefined,
      descending: descending ?? undefined,
      onSort: handleSort as RenderContext["onSort"],
      emptyMessage: t(PAGE_CONFIG.noDataKey),
      searchValue: searchValue ?? "",
      onSearchChange: setSearchValue,
      filterValues,
      onFilterChange,
      dateRangePickerOptions,
      customCellRenderers: {
        // Rate/avg columns need no renderer: the schema's Percentage/Float types produce identical
        // output now that Percentage no longer re-scales an already-computed rate (OR-2436).
        // Tracking/platform source can be long (UTM strings): truncate with a full-value hover tooltip.
        ...Object.fromEntries(
          WEB_ANALYTICS_TRUNCATED_SOURCE_COLUMNS.map((columnKey) => [
            columnKey,
            (value: unknown) => {
              // Empty defers to the schema renderer, keeping showNullAs and its muted styling.
              if (value == null || String(value).trim() === "") return null;
              const full = String(value).trim();
              const truncated = truncateWithEllipsis(full, SOURCE_COLUMN_MAX_LENGTH);
              if (truncated === full) return full;
              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-default">{truncated}</span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-sm break-all">
                    {full}
                  </TooltipContent>
                </Tooltip>
              );
            },
          ]),
        ),
      },
    }),
    [
      clientId,
      tableData,
      aggregatedQuery.isLoading,
      aggregatedQuery.isFetching,
      page,
      pageSize,
      totalRecords,
      setPage,
      setPageSize,
      orderBy,
      descending,
      handleSort,
      t,
      searchValue,
      setSearchValue,
      filterValues,
      onFilterChange,
      dateRangePickerOptions,
    ],
  );

  if (!moduleLoading && !moduleStatus?.enabled) {
    return <UnavailablePage variant="integrationDisabled" />;
  }

  return (
    <ModernPageLayout title={t(PAGE_CONFIG.titleKey)} subtitle={t(PAGE_CONFIG.subtitleKey)}>
      <div className="space-y-4">
        {ui.map((component, index) => (
          <div key={`${component.component}-${index}`} className="space-y-4">
            {component.component === "dataTable" && (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
                {METRICS_CONFIG.map((metric) => {
                  const Icon = METRIC_ICONS[metric.metricKey];
                  const subtitle = metric.secondary
                    // `total`, not i18next's reserved `count` (which is typed as number and drives
                    // pluralization) — the value is a pre-formatted string.
                    ? t(metric.secondary.subtitleKey, {
                      total: formatMetric(metric.secondary.metricKey, metric.secondary.format, metricsQuery.data),
                    })
                    : undefined;
                  return (
                    <MetricCard
                      key={metric.metricKey}
                      icon={<Icon className="h-4 w-4 lg:h-6 lg:w-6 text-primary" />}
                      label={t(metric.labelKey)}
                      value={formatMetric(metric.metricKey, metric.format, metricsQuery.data)}
                      subtitle={subtitle}
                      variant="gradient"
                      isLoading={metricsQuery.isLoading || metricsQuery.isFetching}
                    />
                  );
                })}
              </div>
            )}
            {renderComponent(
              component,
              component.component === "tableToolbar"
                ? { ...context, customToolbarActions: filtersAction }
                : context,
            )}
            {component.component === "tableToolbar" && (
              <TableActiveFilters
                filters={activeFilters}
                onRemoveFilter={removeFilter}
                onClearFilters={clearFilters}
              />
            )}
          </div>
        ))}
      </div>

      <FiltersDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        searchParams={searchParams}
        filterKeys={[...WEB_ANALYTICS_FILTER_DIMENSIONS]}
        filterSections={filterSections}
        entityName={t(PAGE_CONFIG.entityNameKey)}
        filtersLoading={filterCountsQuery.isLoading}
        useFilteredCount={useFilteredCount}
        preserveParamKeys={WEB_ANALYTICS_FILTER_PRESERVE_PARAM_KEYS}
        searchTag={debouncedSearchValue ?? undefined}
      />
    </ModernPageLayout>
  );
}
